import * as React from 'react';

import * as UI from 'core/ui';

import * as Images from 'pos/Images';

import { Result, Binding } from 'core/infrastructure';
import { Preconditions, zDecimal, zDecimalToString } from 'core/infrastructure-next';
import { Command } from 'pos/common';
import { Res, Permission, ProductAvailabilityDTO } from 'core/api';
import { AcceptActionButton, Content, Filler, Footer, Header, Keypad, KeypadActionButton, PosScreen, ThemeManager } from 'pos/ui';
import { CatalogueFacade } from 'pos/facades';
import { Modal } from 'core/ui-next/modal';
import z from 'zod';
import { Box, HStack, VStack, Text, Table, Button, TableScrollArea } from '@chakra-ui/react';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import { SvgIcon } from 'core/ui-next';
import { Close } from 'core/img';
import { CheckBoxMenuItemDef } from 'core/ui-next/controls/CheckBoxMenu';


type QuantityModel = {
	id: number;
	name: string;
	currentQuantity: Nullable<number>;
	newQuantityValue: string;
	hasUnlimitedSale: boolean;
};

type Props = {
	availableQuantities: ProductAvailabilityDTO[]
} & Modal.Actions<ProductAvailabilityDTO[]>;


function getCurrentQuantity(model: QuantityModel): Nullable<number> {
	return model.newQuantityValue !== '' ? Binding.parse(model.newQuantityValue, 'decimal', false).value : null;
}


const stateSchema = z.object({
	criteria: z.string()
});

const formTableStateSchemas = z.object({
	quantities: z.array(z.object({
		id: z.number(),
		name: z.string(),
		currentQuantity: zDecimal({ nonNegative: true, required: false, decimalPlaces: 3 }).nullable(),
		newQuantityValue: zDecimalToString({ nonNegative: true, required: false, decimalPlaces: 3 }),
		hasUnlimitedSale: z.boolean()
	}))
});

type FormState = z.infer<typeof stateSchema>;
type FormTableState = z.infer<typeof formTableStateSchemas>;


function SetProductAvailability(props: Props) {
	const { cancel, close } = props;

	const [showKeypad, setShowKeypad] = React.useState(false);
	const [criteria, setCriteria] = React.useState('');
	const [quantities, setQuantities] = React.useState<QuantityModel[]>(props.availableQuantities.map(q => ({ id: q.Id, name: q.Name, currentQuantity: q.CurrentQuantity, newQuantityValue: q.NewQuantity !== null && q.NewQuantity !== undefined ? q.NewQuantity.toString() : '', hasUnlimitedSale: q.CurrentQuantity === null || q.CurrentQuantity === undefined })));
	const [showLimitedProducts, setShowLimitedProducts] = React.useState(false);
	const [showUnlimitedProducts, setShowUnlimitedProducts] = React.useState(false);
	const [showAllProducts, setShowAllProducts] = React.useState(true);

	// Ref para acceder al estado actual sin recrear callbacks ni forzar setValue dentro de setters.
	const quantitiesRef = React.useRef(quantities);
	React.useEffect(() => { quantitiesRef.current = quantities; }, [quantities]);


	// Formulario principal de la vista para filtrar productos.
	const formRef = React.useRef<FormRef<FormState>>(null);
	const { Form, Input, CheckBoxMenu } = useControlsFor<FormState>();

	const initialState: FormState = React.useMemo(() => ({
		criteria: ''
	}), []);


	// Formulario de la tabla de productos para setear la disponibilidad.
	const formTableRef = React.useRef<FormRef<FormTableState>>(null);
	const { Form: FormTable, CheckBox, DecimalInput } = useControlsFor<FormTableState>();

	const initialTableState: FormTableState = React.useMemo(() => ({
		quantities: quantities
	}), [quantities]);


	// Limpia el criteria de busqueda del formulario (el onWatch resetea `criteria` al string vacío).
	const onClear = React.useCallback(() => {
		formRef.current?.setValue('criteria', '');
	}, []);


	const onSearch = React.useCallback(async () => {
		const result = await formRef.current!.getValue();
		if (!result.ok)
			return;
		setCriteria(result.value.criteria.trim());

	}, []);


	const onOK = React.useCallback(async () => {

		const result = await formTableRef.current!.getValue();

		if (!result.ok)
			return;


		close(result.value.quantities.map(m => {

			// Como necesitamos convertir el valor de newQuantity '' a null,
			// ya que con la validación de zod zDecimalToString el valor '' lo convierte a "0",
			// necesitamos controlar el valor del checkbox hasIlimitedSale, para establecer correctamente NewQuantity 
			// en productos con Venta Ilimitada o cuando es limitada pero el DecimalInput esta vacío.
			let newQuantity: Nullable<number>;
			if (m.hasUnlimitedSale)
				newQuantity = null;
			else if (m.newQuantityValue === '' || m.newQuantityValue === null || m.newQuantityValue === undefined)
				newQuantity = 0;
			else
				newQuantity = Binding.parse(m.newQuantityValue, 'decimal', false).value;

			return {
				Id: m.id,
				Name: m.name,
				CurrentQuantity: m.currentQuantity,
				NewQuantity: newQuantity
			};
		}));
	}, [close]);


	const onShowKeyPad = React.useCallback(async (focus?: boolean) => {
		if (focus && showKeypad) return;
		setShowKeypad(v => !v);
	}, [showKeypad]);


	const onClearClick = React.useCallback(async (e: React.MouseEvent<HTMLButtonElement>, index: number) => {
		e.preventDefault();

		formTableRef.current?.setValue(`quantities.${index}.newQuantityValue`, '');
		setQuantities(prev => {
			const newQuantities = [...prev];
			newQuantities[index].newQuantityValue = '';
			return newQuantities;
		});
	}, []);


	const onDownClick = React.useCallback(async (e: React.MouseEvent<HTMLButtonElement>, index: number) => {
		e.preventDefault();

		const current = quantitiesRef.current[index];
		if (current.hasUnlimitedSale)
			return;

		const currentQuantity = getCurrentQuantity(current) || 0;
		const nextValue = Math.max(currentQuantity - 1, 0).toString();

		formTableRef.current?.setValue(`quantities.${index}.newQuantityValue`, nextValue);
		setQuantities(prev => {
			const newQuantities = [...prev];
			newQuantities[index] = { ...newQuantities[index], newQuantityValue: nextValue };
			return newQuantities;
		});
	}, []);


	const onUpClick = React.useCallback(async (e: React.MouseEvent<HTMLButtonElement>, index: number) => {
		e.preventDefault();

		const current = quantitiesRef.current[index];
		if (current.hasUnlimitedSale)
			return;

		const currentQuantity = getCurrentQuantity(current) || 0;
		const nextValue = (currentQuantity + 1).toString();

		formTableRef.current?.setValue(`quantities.${index}.newQuantityValue`, nextValue);
		setQuantities(prev => {
			const newQuantities = [...prev];
			newQuantities[index] = { ...newQuantities[index], newQuantityValue: nextValue };
			return newQuantities;
		});
	}, []);


	const onCheckChange = React.useCallback(async (value: boolean, index: number) => {
		const current = quantitiesRef.current[index];
		if (current.hasUnlimitedSale === value)
			return;

		if (value)
			formTableRef.current?.setValue(`quantities.${index}.newQuantityValue`, '');

		setQuantities(prev => {
			const newQuantities = [...prev];
			newQuantities[index] = {
				...newQuantities[index],
				hasUnlimitedSale: value,
				newQuantityValue: value ? '' : newQuantities[index].newQuantityValue,
			};
			return newQuantities;
		});
	}, []);


	const onDecimalInputChange = React.useCallback(async (value: string | null, index: number) => {
		const normalized = value ?? '';
		setQuantities(prev => {
			if (prev[index].newQuantityValue === normalized)
				return prev;

			const newQuantities = [...prev];
			newQuantities[index] = { ...newQuantities[index], newQuantityValue: normalized };
			return newQuantities;
		});
	}, []);


	const matchesFilter = React.useCallback((q: QuantityModel) => {
		const trimmedCriteria = criteria.trim().toLocaleLowerCase();
		if (trimmedCriteria !== '' && !q.name.toLocaleLowerCase().includes(trimmedCriteria))
			return false;

		if (showAllProducts) return true;
		if (showLimitedProducts && !q.hasUnlimitedSale) return true;
		if (showUnlimitedProducts && q.hasUnlimitedSale) return true;
		return false;
	}, [criteria, showAllProducts, showLimitedProducts, showUnlimitedProducts]);


	const visibleQuantities = React.useMemo(
		() => quantities
			.map((quantity, index) => ({ quantity, index }))
			.filter(({ quantity }) => matchesFilter(quantity)),
		[quantities, matchesFilter]
	);


	// El CheckBoxMenu es "uncontrolled" internamente (solo lee `checked` en el primer render).
	// Cuando cambiamos su estado de forma programática (p.ej. al marcar "Todos" desmarcamos el resto),
	// incrementamos esta key para forzar el remount y que se pinten los checks acordes al estado actual.
	const [checkMenuKey, setCheckMenuKey] = React.useState<number>(0);
	const remountCheckMenu = React.useCallback(() => setCheckMenuKey(k => k + 1), []);

	const onCheckBoxMenuChange = (option: 'all' | 'limited' | 'unlimited') => {
		setShowAllProducts(option === 'all' ? !showAllProducts : false);
		setShowLimitedProducts(option === 'limited' ? !showLimitedProducts : false);
		setShowUnlimitedProducts(option === 'unlimited' ? !showUnlimitedProducts : false);
		remountCheckMenu();
	};


	const checkBoxMenuItems: CheckBoxMenuItemDef[] = [
		{ value: 'All', title: Res.Common.CHECK_BOX_MENU_ALL_OPTION_LBL(), checked: showAllProducts, onCheckedChange: () => onCheckBoxMenuChange('all') },
		{ value: 'showLimitedProducts', title: Res.Web.SET_PRODUCT_AVAILABILITY_CHECK_MENU_AVAILAVILITY_LIMITED_PRODUCT_CHK(), checked: showLimitedProducts, onCheckedChange: () => onCheckBoxMenuChange('limited') },
		{ value: 'showUnlimitedProducts', title: Res.Web.SET_PRODUCT_AVAILABILITY_CHECK_MENU_AVAILAVILITY_UNLIMITED_PRODUCT_CHK(), checked: showUnlimitedProducts, onCheckedChange: () => onCheckBoxMenuChange('unlimited') }
	];



	return <PosScreen screenId={'SET_PRODUCT_AVAILABILITY'} size={{ width: 1000, height: 720 }} useKeyPad={false}>
		<Header title={Res.Web.SET_PRODUCT_AVAILABILITY()} image={Images.ProductAvailability} onClose={cancel} />
		<Content>
			<VStack w={'full'} h={'full'} padding={'5.5'} gap={'6'}>
				<HStack width={'full'} h={'full'} gap={'0'}>
					<VStack w={'full'} h={'full'} gap={'6'}>
						<VStack w={'full'} h={'32'} gap={'9'}>
							<VStack w={'full'} gap={'9'}>
								<VStack w={'full'} gap={'4'}>
									<Box><Text fontSize={'md'} fontWeight={'semibold'}>{Res.Web.SET_PRODUCT_AVAILABILITY()}</Text></Box>
									<Box><Text fontSize={'sm'}>{Res.Web.SET_PRODUCT_AVAILABILITY_TITLE_TEXT()}</Text></Box>
								</VStack>
							</VStack>
							<HStack width={'full'}>
								<Form ref={formRef} initialValue={initialState} schema={stateSchema} style={{ width: '100%' }}>
									<HStack w={'full'} justifyContent={'space-between'} gap={'9'}>
										<Input
											linkTo="criteria"
											placeholder={Res.Web.SET_PRODUCT_AVAILABILITY_SEARCH_PLACEHOLDER_LBL()}
											onWatch={value => { setCriteria(value.trim()); UI.debounce(onSearch)(); }}
											startElement={
												<SvgIcon src={Images.Search} width={'4'} height={'4'} />
											}
											endElement={
												<SvgIcon src={Close} width={'4'} height={'4'} cursor={'pointer'} onClick={onClear} />
											}
											borderRadius={'4xl'}
											borderColor={'neutral.600'}
											height={'10'}
										/>
										<HStack width={'full'}>
											<SvgIcon src={Images.Filter} width={'6'} height={'6'} cursor={'pointer'} />
											<CheckBoxMenu
												key={checkMenuKey}
												icon={<SvgIcon src={Images.ChevronDown} width={'4'} height={'4'} />}
												items={checkBoxMenuItems}
											/>
										</HStack>
									</HStack>
								</Form>
							</HStack>
						</VStack>
						<VStack w={'full'} h={'full'} overflow={'hidden'}>
							<FormTable ref={formTableRef} initialValue={initialTableState} schema={formTableStateSchemas} style={{ width: '100%', height: '100%' }}>
								<TableScrollArea w={'full'} h={'full'}>
									<Table.Root w={'full'} minH={'0'} striped interactive stickyHeader>
										<Table.Header>
											<Table.Row>
												<Table.ColumnHeader width={'62.5'}>
													<Text textAlign={'start'} fontSize={'sm'} fontWeight={'semibold'} color={{ base: '{colors.neutral.800}', _dark: 'white' }}>{Res.Web.SET_RPODUCT_AVAILAVILITY_PRODUCT_CLM()}</Text>
												</Table.ColumnHeader>
												<Table.ColumnHeader width={'35'}>
													<Text textAlign={'center'} fontSize={'sm'} fontWeight={'semibold'} color={{ base: '{colors.neutral.800}', _dark: 'white' }}>{Res.Web.SET_PRODUCT_AVAILAVILITY_LIMITED_SALE_CLM()}</Text>
												</Table.ColumnHeader>
												<Table.ColumnHeader width={'37.5'}>
													<Text textAlign={'center'} fontSize={'sm'} fontWeight={'semibold'} color={{ base: '{colors.neutral.800}', _dark: 'white' }}>{Res.Web.SET_PRODUCT_AVAILAVILITY_AVAILABLE_CLM()}</Text>
												</Table.ColumnHeader>
												<Table.ColumnHeader width={'22.5'}>
													<Text textAlign={'center'} fontSize={'sm'} fontWeight={'semibold'} color={{ base: '{colors.neutral.800}', _dark: 'white' }}>{ }</Text>
												</Table.ColumnHeader>
											</Table.Row>
										</Table.Header>
										<Table.Body>
											{visibleQuantities.length === 0 && <Table.Row>
												<Table.Cell colSpan={4} maxH={'9'}>
													<Text textAlign={'center'} fontSize={'sm'}>{Res.Web.SET_PRODUCT_AVAILABILITY_NOT_SEARCH_RESULTS_LBL()}</Text>
												</Table.Cell>
											</Table.Row>}
											{visibleQuantities.length > 0 && visibleQuantities.map(({ quantity, index }) => {
												return (
													<Table.Row key={quantity.id}>
														<Table.Cell maxH={'9'} alignItems={'start'}><Text fontSize={'sm'}>{quantity.name}</Text></Table.Cell>
														<Table.Cell maxH={'9'}>
															<Box width={'full'} display={'flex'} justifyContent={'center'} alignItems={'center'}>
																<CheckBox
																	size={'sm'}
																	linkTo={`quantities.${index}.hasUnlimitedSale`}
																	value={quantity.hasUnlimitedSale}
																	onWatch={(value) => onCheckChange(value, index)}
																	label={''}
																	fieldProps={{ width: 'auto', justifyContent: 'center', alignItems: 'center' }}
																	cursor={'pointer'}
																/>
															</Box>
														</Table.Cell>
														<Table.Cell maxH={'9'} alignItems={'center'}>
															<HStack gap={'0'} alignItems={'start'} >
																<Button minW={'7.5'}
																	maxH={'7.5'}
																	maxW={'7.5'}
																	background={{ base: 'neutral.100', _dark: 'neutral.800' }}
																	borderColor={{ base: 'neutral.200', _dark: 'neutral.600' }}
																	borderRadius={'6px 0 0 6px'}
																	_hover={{ base: { background: 'neutral.300' }, _dark: { background: 'neutral.700' }, borderColor: 'transparent' }}
																	disabled={quantity.hasUnlimitedSale}
																	onClick={(e) => onDownClick(e, index)}>
																	<SvgIcon src={Images.Minus} width={'3.5'} height={'3.5'} color={{ base: 'neutral.800', _dark: 'white' }} />
																</Button>
																<DecimalInput
																	linkTo={`quantities.${index}.newQuantityValue`}
																	placeholder={''}
																	disabled={quantity.hasUnlimitedSale}
																	onWatch={(value) => onDecimalInputChange(value, index)}
																	height={'7.5'}
																	borderRadius={'0'}
																	border={'1px solid'}
																	onFocus={() => onShowKeyPad(true)}
																	borderColor={{ base: 'neutral.200', _dark: 'neutral.600' }}
																/>
																<Button
																	minW={'7.5'}
																	maxH={'7.5'}
																	maxW={'7.5'}
																	background={{ base: 'neutral.100', _dark: 'neutral.800' }}
																	borderColor={{ base: 'neutral.200', _dark: 'neutral.600' }}
																	borderRadius={'0 6px 6px 0'}
																	_hover={{ base: { background: 'neutral.300' }, _dark: { background: 'neutral.700' }, borderColor: 'transparent' }}
																	disabled={quantity.hasUnlimitedSale}
																	onClick={(e) => onUpClick(e, index)}>
																	<SvgIcon src={Images.Plus} width={'3.5'} height={'3.5'} color={{ base: 'neutral.800', _dark: 'white' }} />
																</Button>
															</HStack>
														</Table.Cell>
														<Table.Cell maxH={'9'} alignItems={'center'}><Button maxH={'6'} maxW={'16'} variant={'outline'} onClick={(e) => onClearClick(e, index)}>{Res.Common.CLEAR_BTN()}</Button></Table.Cell>
													</Table.Row>
												);
											})}
										</Table.Body>
									</Table.Root>
								</TableScrollArea>
							</FormTable>
						</VStack>
					</VStack>
					<VStack
						justifyContent={'flex-end'}
						h={'full'}
						flex={showKeypad ? '1' : '0'}
						display={showKeypad ? 'flex' : 'none'}
						onMouseDown={(e) => e.preventDefault()}
					>
						<Keypad isFullScreen={false} isLargeDevice={ThemeManager.isLargeDevice()} alwaysVisible />
					</VStack>
				</HStack>
			</VStack>
		</Content >
		<Footer>
			<Filler />
			<KeypadActionButton action={onShowKeyPad} />
			<AcceptActionButton action={onOK} />
		</Footer>
	</PosScreen >;
}


declare global {
	interface CommandType {
		setProductAvailability: () => Promise<Result>
	}
}

Command.register('setProductAvailability', Preconditions.ensure(Permission.ProductAvailability_Set), async (): Promise<Result> => {

	const availableQuantities = await CatalogueFacade.getProductAvailableQuantities();
	const result = await Modal.show(SetProductAvailability, { availableQuantities: availableQuantities });

	if (!result.ok)
		return Result.error();

	await CatalogueFacade.setProductAvailableQuantities(result.value);

	return Result.ok();
});